import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Chip, Stack, Typography, Box } from '@mui/material';
import Grid from '@mui/material/Grid';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import Button from '../components/styledComponents/Button';
import WebViewer from '../doc-view/WebViewer';

import './DocViewPage.css';
import { useAppContext } from '../Store';
import { useAuth } from '../auth/AuthContext';
import { loadDocFile } from '../services';
import { SET_PREVIOUS_VIEWDOC_ID, setViewDoc } from '../reducer';
import { UserRoles, accessPolicy } from '../constants';
import { Translations, logDocumentAccess, userHasAccess } from '../utils/helper';
import { Link } from 'react-router-dom';
import TableSkeleton from './TableSkeleton';
import ApplicableStates from './partials/ApplicableStates';

function renderRelatedLink(item: any) {
  const { line_of_business, bu, business_unit, opu, operating_unit, program_or_product } =
    item._source;

  return (
    <a
      href={`/view/${item._id}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ whiteSpace: 'break-spaces' }}
    >
      {bu || business_unit || '-'} &gt; {opu || operating_unit || '-'} &gt;{' '}
      {program_or_product || '-'} &gt; {line_of_business || '-'}
    </a>
  );
}

const getValue = (value: any) =>
  value !== undefined && value !== null && value !== '' ? value : '-';

type DocViewPageParams = {
  id: string;
};

function DocViewPage() {
  const { id } = useParams<DocViewPageParams>();
  const { state, dispatch } = useAppContext();
  const { user } = useAuth();
  const { isLoading, viewDoc } = state;
  const { downloadUrl, related, metadata = {}, hasError } = viewDoc;

  const [docLoading, setDocLoading] = useState<boolean>(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const getDocFiles = async () => {
      setDocLoading(true);
      const response = await loadDocFile(id!, state);
      response && dispatch(setViewDoc(response as ViewDoc));
      response && dispatch({ type: SET_PREVIOUS_VIEWDOC_ID, payload: response.id });
      setDocLoading(false);
    };

    if (id) {
      getDocFiles();
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [dispatch, id]);

  if (hasError) {
    return isLoading === false ? (
      <p className="doc-error">There's been an error getting this document's details</p>
    ) : null;
  }

  const {
    bu,
    explanation_of_impact,
    form_filing_explanation,
    form_number,
    form_source,
    form_status,
    form_title,
    form_type,
    form_type_of_business,
    form_usage_category,
    form_usage_rule,
    impact_on_coverage,
    issuance_options,
    line_of_business,
    notes,
    opu,
    program_or_product,
    state_filings,
    variable_data_instructions,
    variable_static,
    line_of_business_policy_details,
  } = metadata as any;

  const isAuthorizedToEdit =
    user?.groups && userHasAccess(accessPolicy.form.read, user.groups as UserRoles[]);

  const isAuthorizedToDownload =
    user?.groups &&
    (userHasAccess(accessPolicy.formDownload.read, user.groups as UserRoles[]) ||
      user.groups.includes('Task.OriginalDocumentDownload'));

  const previewComponent = state.viewDoc.hasError ? (
    <div className="doc-header">
      <h3>The requested file is missing or faulty</h3>
    </div>
  ) : (
    <WebViewer source={state.viewDoc.fileUrl} />
  );

  return (
    <Box className="container container" sx={{ m: 2 }}>
      <Box className="content-box" sx={{ pt: 1, minHeight: 'calc(100vh - 85px)' }}>
        {docLoading ? (
          <TableSkeleton />
        ) : (
          <Grid
            container
            spacing={2}
            sx={{ '@media print': { displayPrint: 'block' }, minHeight: 'calc(100vh - 125px)' }}
          >
            <Grid item mobile={12} tablet={6}>
              <div>
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 2, textAlign: 'center' }}>
                  {form_title}
                </Typography>
                <div className="doc-metadata">
                  <div className="doc-info" style={{ marginBottom: '1rem' }}>
                    Form <strong>{form_number}</strong> is a {form_source} {line_of_business}{' '}
                    {form_type}
                  </div>
                  <Stack spacing={4} direction="row" sx={{ width: 1 }}>
                    <p>
                      <strong>Business unit </strong>
                      <br />
                      {getValue(bu)}
                    </p>
                    <p>
                      <strong>Sub business unit </strong>
                      <br />
                      {getValue(opu)}
                    </p>
                    <p>
                      <strong>Program or product </strong>
                      <br />
                      {getValue(program_or_product)}
                    </p>
                    <p>
                      <strong>Business Type </strong>
                      <br />
                      {getValue(form_type_of_business)}
                    </p>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mb: '1rem', mt: 2 }}>
                    <Stack sx={{ width: 0.5 }}>
                      <Typography>
                        <strong>Rules and Usages</strong>
                      </Typography>
                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table sx={{ minWidth: 200 }} size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Form Use Category</TableCell>
                              <TableCell align="right">
                                {getValue(Translations.formUsageCategory(form_usage_category))}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Underwriting Guidance</TableCell>
                              <TableCell align="right">{getValue(form_usage_rule)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Impact On Coverage</TableCell>
                              <TableCell align="right">{getValue(impact_on_coverage)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Form Summary</TableCell>
                              <TableCell align="right">{getValue(explanation_of_impact)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Filing Explanation</TableCell>
                              <TableCell align="right">
                                {getValue(form_filing_explanation)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                    <Stack sx={{ width: 0.5 }}>
                      <Typography>
                        <strong>Implementation Notes</strong>
                      </Typography>
                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table sx={{ minWidth: 200 }} size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Variable Field Instructions</TableCell>
                              <TableCell align="right">
                                {getValue(variable_static)}
                                {getValue(variable_data_instructions) !== '-' &&
                                  `. ${getValue(variable_data_instructions).replace(/\.+$/g, '')}.`}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Issuance Options</TableCell>
                              <TableCell align="right">{getValue(issuance_options)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Line of Business Policy Details</TableCell>
                              <TableCell align="right">
                                {getValue(line_of_business_policy_details)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                          <TableRow>
                            <TableCell>Notes</TableCell>
                            <TableCell align="right">{getValue(notes)}</TableCell>
                          </TableRow>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </Stack>
                  <Stack spacing={1}>
                    <Stack>
                      <ApplicableStates
                        state_filings={state_filings}
                        form_type_of_business={form_type_of_business}
                      />
                    </Stack>
                    <Stack>
                      {(related || []).length !== 0 && (
                        <>
                          <Typography>
                            <strong>Also Used In</strong>
                          </Typography>
                          <Typography fontSize={'14px'}>
                            Links are composed of{' '}
                            <i>
                              Business unit &gt; Sub business unit &gt; Program or Product &gt; Line
                              of Business information
                            </i>
                          </Typography>
                          <TableContainer component={Paper} sx={{ maxHeight: 250 }}>
                            <Table stickyHeader sx={{ minWidth: 0.5 }} size="small">
                              <TableBody>
                                {(related || []).length > 0 &&
                                  related.map((item: any) => (
                                    <TableRow
                                      key={item._id}
                                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                      <TableCell colSpan={4}>{renderRelatedLink(item)}</TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </div>
              </div>
            </Grid>
            <Grid item mobile={12} tablet={6}>
              <Stack height={1}>
                <Stack height={1}>
                  <Stack sx={{ mb: 2 }} spacing={2} direction="row">
                    {isAuthorizedToDownload && (
                      <>
                        {downloadUrl && (
                          <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => logDocumentAccess(form_number, 'download')}
                          >
                            <Button color="primary">Download original document</Button>
                          </a>
                        )}
                        {isAuthorizedToEdit && (
                          <Link to={`/forms/${id}`}>
                            <Button
                              sx={{
                                backgroundColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                },
                              }}
                            >
                              Edit document
                            </Button>
                          </Link>
                        )}
                      </>
                    )}
                    {form_status !== undefined && (
                      <Chip
                        label={form_status}
                        sx={{
                          '&.MuiChip-root': { ml: 'auto' },
                          background: form_status === 'Active' ? 'green' : 'red',
                          color: 'common.white',
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                  {previewComponent}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
}

export default DocViewPage;
